import React, { useMemo } from 'react';
import { GRID_W, GRID_H, COUNTRY_DATA } from '../constants/gameConstants';

export const DynamicMapOverlay = React.memo(({ gameState, players }) => {
  // Border segmentleri hesapla
  const borderSegments = useMemo(() => {
    if (!gameState?.grid || !players.length) return [];

    const segments = [];
    
    for (let y = 0; y < GRID_H; y++) {
      for (let x = 0; x < GRID_W; x++) {
        const index = y * GRID_W + x;
        const owner = gameState.grid[index];
        
        if (owner === -1) continue;
        
        const cellWidth = 100 / GRID_W;
        const cellHeight = 100 / GRID_H;
        const x1 = (x * cellWidth);
        const y1 = (y * cellHeight);
        const x2 = x1 + cellWidth;
        const y2 = y1 + cellHeight;
        
        // Üst sınır
        if (y === 0 || gameState.grid[(y - 1) * GRID_W + x] !== owner) {
          const neighborOwner = y > 0 ? gameState.grid[(y - 1) * GRID_W + x] : -1;
          const isBattleFront = neighborOwner !== -1 && neighborOwner !== owner;
          
          segments.push({
            type: 'line',
            x1: `${x1}%`,
            y1: `${y1}%`,
            x2: `${x2}%`,
            y2: `${y1}%`,
            owner: owner,
            isExternal: y === 0,
            isBattleFront: isBattleFront
          });
        }
        
        // Sağ sınır
        if (x === GRID_W - 1 || gameState.grid[y * GRID_W + (x + 1)] !== owner) {
          const neighborOwner = x < GRID_W - 1 ? gameState.grid[y * GRID_W + (x + 1)] : -1;
          const isBattleFront = neighborOwner !== -1 && neighborOwner !== owner;
          
          segments.push({
            type: 'line',
            x1: `${x2}%`,
            y1: `${y1}%`,
            x2: `${x2}%`,
            y2: `${y2}%`,
            owner: owner,
            isExternal: x === GRID_W - 1,
            isBattleFront: isBattleFront
          });
        }
        
        // Alt sınır
        if (y === GRID_H - 1 || gameState.grid[(y + 1) * GRID_W + x] !== owner) {
          const neighborOwner = y < GRID_H - 1 ? gameState.grid[(y + 1) * GRID_W + x] : -1;
          const isBattleFront = neighborOwner !== -1 && neighborOwner !== owner;
          
          segments.push({
            type: 'line',
            x1: `${x1}%`,
            y1: `${y2}%`,
            x2: `${x2}%`,
            y2: `${y2}%`,
            owner: owner,
            isExternal: y === GRID_H - 1,
            isBattleFront: isBattleFront
          });
        }
        
        // Sol sınır
        if (x === 0 || gameState.grid[y * GRID_W + (x - 1)] !== owner) {
          const neighborOwner = x > 0 ? gameState.grid[y * GRID_W + (x - 1)] : -1;
          const isBattleFront = neighborOwner !== -1 && neighborOwner !== owner;
          
          segments.push({
            type: 'line',
            x1: `${x1}%`,
            y1: `${y1}%`,
            x2: `${x1}%`,
            y2: `${y2}%`,
            owner: owner,
            isExternal: x === 0,
            isBattleFront: isBattleFront
          });
        }
      }
    }
    
    return segments;
  }, [gameState?.grid, players]);

  // Bayraklar
  const centerFlags = useMemo(() => {
    if (!gameState?.grid || !players.length) return [];

    const flags = [];

    players.forEach((player, playerIdx) => {
      const playerCells = gameState.grid
        .map((owner, idx) => owner === playerIdx ? idx : -1)
        .filter(idx => idx !== -1);

      if (playerCells.length === 0) return;

      const visited = new Set();
      const regions = [];

      playerCells.forEach(cellIdx => {
        if (visited.has(cellIdx)) return;

        const region = [];
        const queue = [cellIdx];
        visited.add(cellIdx);

        while (queue.length > 0) {
          const idx = queue.shift();
          region.push(idx);

          const x = idx % GRID_W;
          const y = Math.floor(idx / GRID_W);

          const neighbors = [
            y > 0 ? (y - 1) * GRID_W + x : -1,
            y < GRID_H - 1 ? (y + 1) * GRID_W + x : -1,
            x > 0 ? y * GRID_W + (x - 1) : -1,
            x < GRID_W - 1 ? y * GRID_W + (x + 1) : -1
          ];

          neighbors.forEach(nIdx => {
            if (nIdx !== -1 && 
                !visited.has(nIdx) && 
                gameState.grid[nIdx] === playerIdx) {
              visited.add(nIdx);
              queue.push(nIdx);
            }
          });
        }

        regions.push(region);
      });

      const largestRegion = regions.reduce((largest, current) => 
        current.length > largest.length ? current : largest
      );

      let sumX = 0, sumY = 0;
      largestRegion.forEach(idx => {
        sumX += idx % GRID_W;
        sumY += Math.floor(idx / GRID_W);
      });
      
      const centerX = (sumX / largestRegion.length + 0.5) / GRID_W * 100;
      const centerY = (sumY / largestRegion.length + 0.5) / GRID_H * 100;

      const flagSize = Math.min(10, Math.max(5, Math.sqrt(largestRegion.length) * 1.5));

      const flagUrl = player.country 
        ? COUNTRY_DATA[player.country]?.flag 
        : 'https://flagcdn.com/w80/xx.png';

      flags.push({
        playerIdx,
        player,
        centerX,
        centerY,
        flagSize,
        flagUrl,
        cellCount: playerCells.length,
        largestRegionSize: largestRegion.length,
        totalRegions: regions.length
      });
    });

    return flags;
  }, [gameState?.grid, players]);

  if (!gameState || !players.length) {
    return null;
  }

  const getCountryColor = (ownerIndex) => {
    if (ownerIndex === -1) return 'transparent';
    return players[ownerIndex]?.color || '#808080';
  };

  return (
    <svg
      className="absolute top-0 left-0 w-full h-full pointer-events-none z-20"
      style={{
        overflow: 'visible',
        filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.4))'
      }}
    >
      <defs>
        {/* Normal sınır efekti */}
        <filter id="border-glow">
          <feGaussianBlur stdDeviation="0.5" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* 🔥 Savaş hattı için özel efekt */}
        <filter id="battle-line-glow">
          <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
          <feColorMatrix 
            in="coloredBlur"
            type="matrix"
            values="1 0 0 0 0
                    0 0.5 0 0 0
                    0 0 0.5 0 0
                    0 0 0 1.2 0"
          />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Dış sınır glow */}
        <filter id="external-border-glow">
          <feGaussianBlur stdDeviation="1" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Bayrak gölgesi */}
        <filter id="flag-shadow">
          <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
          <feOffset dx="0" dy="2" result="offsetblur" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.5" />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* 🔥 SAVAŞ HATTI ÇİZGİLERİ - Önce çizilir (en altta) */}
      <g className="battle-front-lines">
        {borderSegments
          .filter(seg => seg.isBattleFront && !seg.isExternal)
          .map((seg, idx) => (
            <line
              key={`battle-${idx}`}
              x1={seg.x1}
              y1={seg.y1}
              x2={seg.x2}
              y2={seg.y2}
              stroke="#ff4444"
              strokeWidth="2.5"
              opacity="0.9"
              strokeLinecap="round"
              strokeDasharray="4 2"
              filter="url(#battle-line-glow)"
              style={{
                transition: 'all 0.3s ease'
              }}
            >
              {/* Animasyonlu dash efekti */}
              <animate
                attributeName="stroke-dashoffset"
                from="0"
                to="12"
                dur="1.5s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0.6;1;0.6"
                dur="2s"
                repeatCount="indefinite"
              />
            </line>
          ))}
      </g>

      {/* İç Sınırlar - Normal ülke sınırları */}
      <g className="map-borders-internal">
        {borderSegments
          .filter(seg => !seg.isBattleFront && !seg.isExternal)
          .map((seg, idx) => (
            <line
              key={`internal-${idx}`}
              x1={seg.x1}
              y1={seg.y1}
              x2={seg.x2}
              y2={seg.y2}
              stroke={getCountryColor(seg.owner)}
              strokeWidth="1.5"
              opacity="0.6"
              strokeLinecap="round"
              filter="url(#border-glow)"
              style={{
                transition: 'all 0.3s ease'
              }}
            />
          ))}
      </g>

      {/* Dış Sınırlar - Harita Kenarları */}
      <g className="map-borders-external">
        {borderSegments
          .filter(seg => seg.isExternal)
          .map((seg, idx) => (
            <line
              key={`external-${idx}`}
              x1={seg.x1}
              y1={seg.y1}
              x2={seg.x2}
              y2={seg.y2}
              stroke={getCountryColor(seg.owner)}
              strokeWidth="2"
              opacity="0.8"
              strokeLinecap="round"
              filter="url(#external-border-glow)"
              style={{
                transition: 'all 0.3s ease'
              }}
            />
          ))}
      </g>

      {/* MERKEZ BAYRAKLAR */}
      <g className="country-center-flags">
        {centerFlags.map((flag) => (
          <g key={`flag-${flag.playerIdx}`}>
            <circle
              cx={`${flag.centerX}%`}
              cy={`${flag.centerY}%`}
              r={`${flag.flagSize * 0.85}%`}
              fill={flag.player.color}
              opacity="0.9"
              style={{ 
                transition: 'all 0.4s ease',
                filter: 'blur(32px)'
              }}
            />

            <circle
              cx={`${flag.centerX}%`}
              cy={`${flag.centerY}%`}
              r={`${flag.flagSize * 0.75}%`}
              fill="rgba(0, 0, 0, 0.3)"
              stroke={flag.player.color}
              strokeWidth="1"
              opacity="1"
              style={{ transition: 'all 0.4s ease' }}
            />

            <image
              x={`${flag.centerX - flag.flagSize * 0.65}%`}
              y={`${flag.centerY - flag.flagSize * 0.45}%`}
              width={`${flag.flagSize * 1.3}%`}
              height={`${flag.flagSize * 0.9}%`}
              href={flag.flagUrl}
              filter="url(#flag-shadow)"
              style={{
                pointerEvents: 'none',
                transition: 'all 0.4s ease',
                userSelect: 'none'
              }}
              preserveAspectRatio="xMidYMid meet"
            />     
          </g>
        ))}
      </g>
    </svg>
  );
});

export default DynamicMapOverlay;